import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Input, Button, DatePicker, Alert } from 'antd';
import axios from 'axios';
import { Encrypt } from '../../utils/encryption';
import { userRegistration } from '../../config/api';
import {
  PrefixFormItem,
  mobileForApi,
  isMobileLengthValid,
  MOBILE_MAX_LEN,
  mobileFieldInputPlain,
  emailInputPlain,
  userNameDisplayFromPrefix,
} from '../../constants/CountryCodes';
import { AUTH } from '../../config/zinihubConfig';
import { setStep1Data, setStep2Data, setStep3Data, resetRegisterData } from '../../store/registerSlice';
import styles from './Register.module.css';

function OtpInput({ value, onChange, disabled, error, setError }) {
  const inputRefs = useRef([]);
  const digits = (value || '').split('').concat(Array(6).fill('')).slice(0, 6);

  const handleChange = (idx, e) => {
    const char = e.target.value;
    if (!char) return;
    const newVal = (value || '').split('');
    newVal[idx] = char.slice(0, 1);
    onChange(newVal.join('').slice(0, 6));
    if (idx < 5) {
      const next = inputRefs.current[idx + 1];
      if (next) setTimeout(() => next.focus(), 10);
    }
    if (setError) setError(null);
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace') {
      const newVal = (value || '').split('');
      if (newVal[idx]) {
        newVal[idx] = '';
        onChange(newVal.join(''));
      } else if (idx > 0) {
        newVal[idx - 1] = '';
        onChange(newVal.join(''));
        inputRefs.current[idx - 1]?.focus();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = (e.clipboardData?.getData('text') || '').slice(0, 6);
    onChange(paste);
    const lastIdx = Math.min(paste.length, 5);
    setTimeout(() => inputRefs.current[lastIdx]?.focus(), 10);
    if (setError) setError(null);
  };

  return (
    <div className={styles.otpDigitBox}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => { inputRefs.current[i] = el; }}
          type="text"
          className={styles.otpDigit}
          maxLength={1}
          value={d}
          disabled={disabled}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={i === 0 ? handlePaste : undefined}
          autoComplete="one-time-code"
        />
      ))}
    </div>
  );
}

function Register() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const registrationFormData = useSelector((s) => s.register);

  const [step, setStep] = useState(1);

  const [mobileLoading, setMobileLoading] = useState(false);
  const [mobileResendLoading, setMobileResendLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailResendLoading, setEmailResendLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const [mobileResend, setMobileResend] = useState(0);
  const [emailResend, setEmailResend] = useState(0);

  const [showMobileOtp, setShowMobileOtp] = useState(false);
  const [showEmailOtp, setShowEmailOtp] = useState(false);
  const [mobileVerified, setMobileVerified] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);

  const [mobileOtp, setMobileOtp] = useState('');
  const [emailOtp, setEmailOtp] = useState('');
  const [mobileToken, setMobileToken] = useState(null);
  const [confirmOtpLoading, setConfirmOtpLoading] = useState(false);
  const [mobileOtpVerified, setMobileOtpVerified] = useState(false);

  const [apiError, setApiError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [mobileOtpError, setMobileOtpError] = useState(null);
  const [emailOtpError, setEmailOtpError] = useState(null);

  const mobileRef = useRef(null);
  const emailRef = useRef(null);

  const [form] = Form.useForm();
  const mobileWatch = Form.useWatch('mobile', form);
  const emailWatch = Form.useWatch('email', form);

  useEffect(() => {
    dispatch(resetRegisterData());
  }, [dispatch]);

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v || '');

  const fmtCountdown = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  useEffect(() => {
    if (mobileResend > 0) {
      const t = setTimeout(() => setMobileResend((c) => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [mobileResend]);

  useEffect(() => {
    if (emailResend > 0) {
      const t = setTimeout(() => setEmailResend((c) => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [emailResend]);

  const resetMobileOtp = () => {
    setMobileOtp('');
    setShowMobileOtp(false);
    setMobileToken(null);
    setMobileVerified(false);
    setMobileOtpVerified(false);
    setMobileOtpError(null);
    dispatch(setStep1Data({ prefix: registrationFormData.prefix, mobile: registrationFormData.mobile, verifiedMobileToken: null }));
    form.setFieldsValue({ userName: '' });
  };

  const confirmMobileOtp = async () => {
    if (!mobileToken || mobileOtp.length < 6) return;
    const prefix = form.getFieldValue('prefix');
    const val = form.getFieldValue('mobile');
    setApiError(null);
    setConfirmOtpLoading(true);
    let verified = false;
    try {
      const { data } = await axios.post(
        userRegistration,
        Encrypt({
          action: 'verifyMobileOTP',
          mobile: mobileForApi(prefix, val),
          token: mobileToken,
          mobileVerificationCode: mobileOtp,
        }),
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (data?.verifiedMobileToken) {
        verified = true;
        setMobileOtpVerified(true);
        setMobileOtpError(null);
        dispatch(setStep1Data({ prefix, mobile: val, verifiedMobileToken: data.verifiedMobileToken }));
        setTimeout(() => {
          setConfirmOtpLoading(false);
          setStep(2);
        }, 1200);
      } else {
        setApiError('Verification failed. Please re-verify.');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || AUTH.REGISTER_VERIFY_FAILED;
      setApiError(msg);
    } finally {
      if (!verified) setConfirmOtpLoading(false);
    }
  };

  const verifyMobile = async (action = '') => {
    const prefix = form.getFieldValue('prefix');
    const val = form.getFieldValue('mobile');

    if (mobileVerified && action !== 'Resend') {
      resetMobileOtp();
      return;
    }
    if (action === 'Resend') setMobileOtp('');

    if (!isMobileLengthValid(val)) return;

    setApiError(null);
    setMobileOtpError(null);
    setMobileOtpVerified(false);

    const isResend = action === 'Resend';
    if (isResend) setMobileResendLoading(true);
    else setMobileLoading(true);
    try {
      const { data } = await axios.post(
        userRegistration,
        Encrypt({ action: 'verifyMobile', mobile: mobileForApi(prefix, val) }),
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (data?.token) {
        setShowMobileOtp(true);
        setMobileToken(data.token);
        setMobileVerified(true);
        form.setFieldsValue({ userName: userNameDisplayFromPrefix(prefix, val) });
        setMobileResend(46);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || AUTH.REGISTER_VERIFY_FAILED;
      setApiError(msg);
    } finally {
      if (isResend) setMobileResendLoading(false);
      else setMobileLoading(false);
    }
  };

  const resetEmailOtp = () => {
    setEmailOtp('');
    setShowEmailOtp(false);
    setEmailVerified(false);
    setEmailOtpError(null);
  };

  const verifyEmail = async (action = '') => {
    const val = (form.getFieldValue('email') || '').trim();

    if (emailVerified && action !== 'Resend') {
      resetEmailOtp();
      return;
    }
    if (action === 'Resend') setEmailOtp('');

    if (!val || !isValidEmail(val)) return;

    setApiError(null);
    setEmailOtpError(null);

    const isResend = action === 'Resend';
    if (isResend) setEmailResendLoading(true);
    else setEmailLoading(true);
    try {
      const { data } = await axios.post(
        userRegistration,
        Encrypt({ action: 'verifyEmail', email: val }),
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (data?.token) {
        setShowEmailOtp(true);
        setEmailVerified(true);
        setEmailResend(46);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || AUTH.REGISTER_VERIFY_FAILED;
      setApiError(msg);
    } finally {
      if (isResend) setEmailResendLoading(false);
      else setEmailLoading(false);
    }
  };

  const submitRegistration = async () => {
    try {
      await form.validateFields(['email']);
    } catch {
      return;
    }
    const { email, dateOfBirth } = form.getFieldsValue(['email', 'dateOfBirth']);
    const emailStr = (email || '').trim();

    setApiError(null);
    setSubmitLoading(true);
    try {
      const { data } = await axios.post(
        userRegistration,
        Encrypt({
          action: 'user-registration',
          verifiedMobileToken: registrationFormData.verifiedMobileToken,
          mobile: mobileForApi(registrationFormData.prefix, registrationFormData.mobile),
          userName: `+${mobileForApi(registrationFormData.prefix, registrationFormData.mobile)}`,
          firstName: registrationFormData.firstName,
          lastName: registrationFormData.lastName,
          password: registrationFormData.password,
          confirmPassword: registrationFormData.password,
          email: emailStr,
          dateOfBirth: dateOfBirth ? dateOfBirth.format('YYYY-MMM-DD') : '',
        }),
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (data?.message?.toLowerCase().includes('success')) {
        setSuccessMessage(data.message);
        dispatch(resetRegisterData());
        setSubmitLoading(false);
        setTimeout(() => navigate('/login'), 2000);
        return;
      } else {
        setApiError(data?.message || AUTH.REGISTER_ERROR_UNKNOWN);
      }
    } catch (err) {
      setApiError(err?.response?.data?.message || AUTH.REGISTER_ERROR_UNKNOWN);
    } finally {
      setSubmitLoading(false);
    }
  };

  const canContinue1 = mobileOtp.length === 6 || mobileOtpVerified;

  return (
    <>
      {apiError && (
        <Alert message={apiError} type="error" showIcon closable className={styles.alert} onClose={() => setApiError(null)} />
      )}
      {successMessage && (
        <Alert message={successMessage} type="success" showIcon className={styles.alert} />
      )}

      <div className={styles.subtitle}>{AUTH.REGISTER_TITLE}</div>

      <div className={styles.steps}>
        {[1, 2, 3].map((n) => (
          <div
            key={n}
            className={`${styles.stepDot} ${n <= step ? styles.stepDotActive : styles.stepDotInactive}`}
          />
        ))}
      </div>

      {step === 1 && (
        <>
          <div className={styles.stepLabel}>{AUTH.REGISTER_STEP1_LABEL}</div>
          <div className={styles.stepDesc}>{AUTH.REGISTER_STEP1_DESC}</div>
        </>
      )}
      {step === 2 && (
        <>
          <div className={styles.stepLabel}>{AUTH.REGISTER_STEP2_LABEL}</div>
          <div className={styles.stepDesc}>{AUTH.REGISTER_STEP2_DESC}</div>
        </>
      )}
      {step === 3 && (
        <>
          <div className={styles.stepLabel}>{AUTH.REGISTER_STEP3_LABEL}</div>
          <div className={styles.amberBox}>
            <span>{AUTH.REGISTER_STEP3_AMBER}</span>
          </div>
        </>
      )}

      <Form form={form} layout="vertical" initialValues={{ prefix: '+91' }}>
        {/* ── Step 1 fields ── */}
        {step === 1 && (
          <>
            <div className={styles.fieldGroup}>
              <div className={styles.fieldLabel}>{AUTH.REGISTER_MOBILE_LABEL}</div>
              <div className={styles.mobileRow}>
                <div className={styles.countryCode}>
                  <PrefixFormItem disabled={mobileVerified} />
                </div>
                <Form.Item
                  name="mobile"
                  className={styles.mobileInput}
                  rules={[
                    { required: true, message: AUTH.REGISTER_MOBILE_REQUIRED },
                    { pattern: /^\d+$/, message: AUTH.REGISTER_MOBILE_DIGITS },
                    {
                      validator: (_, value) =>
                        !value || isMobileLengthValid(value)
                          ? Promise.resolve()
                          : Promise.reject(new Error(AUTH.REGISTER_MOBILE_INVALID)),
                    },
                  ]}
                >
                  <Input
                    ref={mobileRef}
                    disabled={mobileVerified}
                    maxLength={MOBILE_MAX_LEN}
                    style={mobileFieldInputPlain}
                    placeholder={AUTH.LOGIN_PHONE_PLACEHOLDER}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '').slice(0, MOBILE_MAX_LEN);
                      form.setFieldsValue({ mobile: v });
                    }}
                  />
                </Form.Item>
                <Form.Item className={styles.verifyBtn}>
                  <Button
                    size="small"
                    className={`${styles.verifyBtn} ${styles.verifyBtnGhost}`}
                    onClick={() => verifyMobile(mobileVerified ? 'Edit' : '')}
                    disabled={mobileLoading || mobileResendLoading || !isMobileLengthValid(mobileWatch)}
                    loading={mobileLoading}
                  >
                    {mobileLoading ? AUTH.REGISTER_VERIFY_SENDING : mobileVerified ? AUTH.REGISTER_EDIT : AUTH.REGISTER_VERIFY}
                  </Button>
                </Form.Item>
              </div>

              {showMobileOtp && (
                <div className={styles.otpBox}>
                    <div className={styles.otpHeader}>{AUTH.REGISTER_OTP_HEADER}</div>
                  <OtpInput
                    value={mobileOtp}
                    onChange={setMobileOtp}
                    disabled={mobileResendLoading || mobileOtpVerified}
                    error={mobileOtpError}
                    setError={setMobileOtpError}
                  />
                  {mobileOtpError && <div className={styles.otpMsg}>{mobileOtpError}</div>}
                  {mobileOtpVerified && <div className={styles.otpSuccess}>Verified ✓</div>}
                  <div className={styles.otpFooter}>
                    <span
                      className={mobileResend > 0 ? styles.otpActionDisabled : styles.otpAction}
                      onClick={() => mobileResend <= 0 && !mobileResendLoading && verifyMobile('Resend')}
                    >
                      {mobileResend > 0 ? `Resend in ${fmtCountdown(mobileResend)}` : AUTH.REGISTER_OTP_RESEND}
                    </span>
                    <span className={styles.otpSeparator}>·</span>
                    <span className={styles.otpAction} onClick={resetMobileOtp}>{AUTH.REGISTER_OTP_CHANGE}</span>
                  </div>
                </div>
              )}
            </div>

            <div className={styles.navRow}>
              <Button
                type="primary"
                className={styles.navBtn}
                style={{ flex: 1 }}
                disabled={!canContinue1 || confirmOtpLoading}
                loading={confirmOtpLoading}
                onClick={() => {
                  if (mobileOtpVerified) {
                    setStep(2);
                  } else {
                    confirmMobileOtp();
                  }
                }}
              >
                {AUTH.REGISTER_CONTINUE}
              </Button>
            </div>

            <div className={styles.loginLink}>
              {AUTH.REGISTER_LOGIN_LINK}<span className={styles.linkLike} onClick={() => navigate('/login')}>{AUTH.REGISTER_LOGIN_CTA}</span>
            </div>
          </>
        )}

        {/* ── Step 2 fields ── */}
        {step === 2 && (
          <>
            <div className={styles.colRow}>
              <div className={styles.colHalf}>
                <Form.Item
                  label={AUTH.REGISTER_FIRST_NAME} name="firstName"
                  rules={[{ required: true, message: AUTH.REGISTER_FIRST_NAME_REQUIRED }]}
                  className={styles.fieldItem}
                >
                  <Input />
                </Form.Item>
              </div>
              <div className={styles.colHalf}>
                <Form.Item
                  label={AUTH.REGISTER_LAST_NAME} name="lastName"
                  rules={[{ required: true, message: AUTH.REGISTER_LAST_NAME_REQUIRED }]}
                  className={styles.fieldItem}
                >
                  <Input />
                </Form.Item>
              </div>
            </div>

            <Form.Item
              label={AUTH.REGISTER_PASSWORD_LABEL} name="password"
              rules={[
                { required: true, message: AUTH.REGISTER_PASSWORD_REQUIRED },
                { min: 5, message: AUTH.REGISTER_PASSWORD_MINLENGTH },
              ]}
              className={styles.passwordField}
            >
              <Input.Password className={styles.passwordInput} />
            </Form.Item>
            <span className={styles.hintText}>{AUTH.REGISTER_PASSWORD_HINT}</span>

            <Form.Item
              label={AUTH.REGISTER_CONFIRM_PASSWORD} name="confirmPassword"
              dependencies={['password']}
              rules={[
                { required: true, message: AUTH.REGISTER_CONFIRM_PASSWORD_REQUIRED },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    return !value || getFieldValue('password') === value
                      ? Promise.resolve()
                      : Promise.reject(new Error(AUTH.REGISTER_PASSWORDS_MISMATCH));
                  },
                }),
              ]}
              className={styles.passwordField}
            >
              <Input.Password className={styles.passwordInput} />
            </Form.Item>

            <div className={styles.navRow}>
              <Button className={styles.navBtn} style={{ flex: 1 }} onClick={() => {
                dispatch(setStep2Data({
                  firstName: form.getFieldValue('firstName'),
                  lastName: form.getFieldValue('lastName'),
                  password: form.getFieldValue('password'),
                  confirmPassword: form.getFieldValue('confirmPassword'),
                }));
                form.setFieldsValue({ prefix: registrationFormData.prefix, mobile: registrationFormData.mobile });
                setStep(1);
              }}>
                {AUTH.REGISTER_BACK}
              </Button>
              <Button
                type="primary" className={styles.navBtn} style={{ flex: 1 }}
                onClick={() => {
                  form.validateFields(['firstName', 'lastName', 'password', 'confirmPassword'])
                    .then(() => {
                      dispatch(setStep2Data({
                        firstName: form.getFieldValue('firstName'),
                        lastName: form.getFieldValue('lastName'),
                        password: form.getFieldValue('password'),
                        confirmPassword: form.getFieldValue('confirmPassword'),
                      }));
                      setStep(3);
                    })
                    .catch(() => {});
                }}
              >
                {AUTH.REGISTER_CONTINUE}
              </Button>
            </div>
          </>
        )}

        {/* ── Step 3 fields ── */}
        {step === 3 && (
          <>
            <Form.Item
              label={AUTH.REGISTER_USERNAME_LABEL} name="userName"
              rules={[{ required: true, message: AUTH.REGISTER_USERNAME_REQUIRED }]}
              className={styles.fieldItem}
            >
              <Input disabled />
            </Form.Item>
            <span className={styles.hintText}>{AUTH.REGISTER_USERNAME_HINT}</span>

            <Form.Item label={AUTH.REGISTER_EMAIL_LABEL} required className={styles.fieldItem}>
              <div className={styles.mobileRow}>
                <Form.Item
                  name="email" noStyle
                  rules={[
                    { required: true, whitespace: true, message: AUTH.REGISTER_EMAIL_REQUIRED },
                    {
                      validator: (_, value) => {
                        const s = (value || '').trim();
                        if (!s) return Promise.resolve();
                        return isValidEmail(s) ? Promise.resolve() : Promise.reject(new Error(AUTH.REGISTER_EMAIL_INVALID));
                      },
                    },
                  ]}
                  style={{ flex: 1, minWidth: 0 }}
                >
                    <Input
                      type="email" ref={emailRef} disabled={emailVerified}
                      style={emailInputPlain} placeholder={AUTH.REGISTER_EMAIL_PLACEHOLDER} autoComplete="email"
                  />
                </Form.Item>
                <Form.Item className={styles.verifyBtn}>
                  <Button
                    size="small" className={`${styles.verifyBtn} ${styles.verifyBtnGhost}`}
                    onClick={() => verifyEmail(emailVerified ? 'Edit' : '')}
                    disabled={emailLoading || emailResendLoading || !(emailWatch || '').trim() || !isValidEmail((emailWatch || '').trim())}
                    loading={emailLoading}
                  >
                    {emailLoading ? AUTH.REGISTER_VERIFY_SENDING : emailVerified ? AUTH.REGISTER_EDIT : AUTH.REGISTER_VERIFY}
                  </Button>
                </Form.Item>
              </div>

              {showEmailOtp && (
                <div className={styles.otpBox}>
                  <div className={styles.otpHeader}>{AUTH.REGISTER_EMAIL_OTP_HEADER}</div>
                  <OtpInput
                    value={emailOtp}
                    onChange={setEmailOtp}
                    disabled={emailResendLoading}
                    error={emailOtpError}
                    setError={setEmailOtpError}
                  />
                  {emailOtpError && <div className={styles.otpMsg}>{emailOtpError}</div>}
                  <div className={styles.otpFooter}>
                    <span
                      className={emailResend > 0 ? styles.otpActionDisabled : styles.otpAction}
                      onClick={() => emailResend <= 0 && !emailResendLoading && verifyEmail('Resend')}
                    >
                      {emailResend > 0 ? `Resend in ${fmtCountdown(emailResend)}` : AUTH.REGISTER_OTP_RESEND}
                    </span>
                    <span className={styles.otpSeparator}>·</span>
                    <span className={styles.otpAction} onClick={resetEmailOtp}>{AUTH.REGISTER_EMAIL_CHANGE}</span>
                  </div>
                </div>
              )}
              <span className={styles.hintText}>{AUTH.REGISTER_EMAIL_HINT}</span>
            </Form.Item>

            <Form.Item label={AUTH.REGISTER_DOB_LABEL} name="dateOfBirth" className={styles.fieldItem}>
              <DatePicker
                format="YYYY-MMM-DD" style={{ width: '100%' }}
                placeholder={AUTH.REGISTER_DOB_PLACEHOLDER} allowClear
                disabledDate={(current) => current && current >= new Date().setHours(0, 0, 0, 0)}
              />
            </Form.Item>

            <div className={styles.navRow}>
              <Button className={styles.navBtn} style={{ flex: 1 }} onClick={() => {
                dispatch(setStep3Data({
                  email: form.getFieldValue('email'),
                  dateOfBirth: form.getFieldValue('dateOfBirth'),
                }));
                form.setFieldsValue({
                  firstName: registrationFormData.firstName,
                  lastName: registrationFormData.lastName,
                  password: registrationFormData.password,
                  confirmPassword: registrationFormData.confirmPassword,
                });
                setStep(2);
              }}>
                {AUTH.REGISTER_BACK}
              </Button>
              <Button
                type="primary" className={styles.navBtn} style={{ flex: 1 }}
                loading={submitLoading}
                onClick={submitRegistration}
              >
                {submitLoading ? AUTH.REGISTER_SUBMITTING : AUTH.REGISTER_SUBMIT}
              </Button>
            </div>
          </>
        )}
      </Form>
    </>
  );
}

export default Register;
